import { useEffect, useRef, useState } from 'react';

import type { FormikBag, FormikErrors } from 'formik';
import { Form, useFormikContext, withFormik } from 'formik';
import { connect, useDispatch } from 'react-redux';
import { compose } from 'recompact';

import type { UnifiedEvent } from 'src/app/analytics/analyticsEvents';
import { UNIFIED } from 'src/app/analytics/analyticsEvents';
import { DevOnlyRegistrationInfo } from 'src/app/components/Elements/DevOnly/DevOnlyRegistrationInfo';
import LinkWithQuery from 'src/app/components/Elements/LinkWithQuery';
import { useFocusOnFirstFormError } from 'src/app/components/Form/Hooks/useFocusOnFirstFormError';
import TextInput from 'src/app/components/Form/TextInput';
import { Step } from 'src/app/components/Pages/GetStarted/StepLayout/Step';
import { login } from 'src/app/router/paths';
import type { LeadAttributes, SegmentTrackType } from 'src/app/state/actions';
import {
  addOnboardingStepAfterGivenStepKey,
  createLeadAction,
  emailCheck,
  segmentTrack,
  setOnboardingFormError,
  setOnboardingFormValues,
} from 'src/app/state/actions';
import type { OnboardingForm } from 'src/app/state/reducers';
import { getOnboardingForm } from 'src/app/state/selectors';
import type { Dispatch, RootState } from 'src/app/state/store';
import { useSelector } from 'src/app/state/store';

import type { ProgressStep } from '../../StepLayout/consts';
import { CAPTCHA_STEP, STEP_KEY } from '../../StepLayout/consts';
import { ButtonContainer, LegalMessage, StyledButton } from '../../styles';
import { setOnboardingStorage } from '../../util';

import { TermsOfService } from './TermsOfService';
import { validation } from './validation';

type FormValues = {
  email: string;
};

interface StepHandler {
  errors: FormikErrors<FormValues>;
  handleSubmit: () => void;
  isSubmitting: boolean;
  onSetOnboardingFormValues: (arg: FormValues) => void;
  segmentTrack: SegmentTrackType;
  setShowHorizontalProgressBar: (show: boolean) => void;
}

const UnconnectedContactStep = ({
  errors,
  handleSubmit,
  isSubmitting,
  onSetOnboardingFormValues,
  segmentTrack,
  setShowHorizontalProgressBar,
}: StepHandler) => {
  const { setErrors, isValid, isValidating, values, setFieldTouched } = useFormikContext<FormValues>();
  const dispatch = useDispatch();
  const formRef = useRef(null);

  const { error: onboardingFormError, email: onboardingFormEmail } = useSelector(getOnboardingForm);
  const { email: formEmail } = values;

  useEffect(() => {
    if (onboardingFormError) {
      setErrors({ email: onboardingFormError });
      setFieldTouched('email', true);
    }

    if (formEmail !== onboardingFormEmail) {
      dispatch(setOnboardingFormError(undefined));
    }
  }, [
    dispatch,
    formEmail,
    onboardingFormEmail,
    onboardingFormError,
    setErrors,
    setFieldTouched,
    values,
    isValidating,
    isValid,
  ]);

  setShowHorizontalProgressBar(true);
  useFocusOnFirstFormError(formRef, errors, isSubmitting, isValidating, false);

  const [dispatchedEvent, setDispatchedEvent] = useState(false);

  useEffect(() => {
    if (!dispatchedEvent) {
      segmentTrack(UNIFIED.viewOnboardingContactInfo, { contact_type: 'email' });
      setDispatchedEvent(true);
    }
  }, [dispatchedEvent, segmentTrack]);

  const onBlur = () => {
    const lowerCaseEmail = values.email?.toLowerCase();
    onSetOnboardingFormValues({ email: lowerCaseEmail });
  };

  return (
    <Step
      title={`Great! Add your email address`}
      text={`Enter your email so we can save your progress`}
      customHandlePrevText={'Delivery'}
    >
      <Form ref={formRef}>
        <TextInput name="email" label="Email" type="text" onBlur={onBlur} />
        <ButtonContainer smallMarginTop={true}>
          <StyledButton
            size="medium"
            disabled={!isValid || isSubmitting}
            onClick={handleSubmit}
            type="submit"
            style={'capsuleBlue50'}
          >
            Continue
          </StyledButton>
        </ButtonContainer>
        <LegalMessage>
          <TermsOfService />
        </LegalMessage>
      </Form>
      <DevOnlyRegistrationInfo />
    </Step>
  );
};

const mapStateToProps = (state: RootState) => {
  return {
    email: (state.onboardingForm as OnboardingForm).email,
    zipCode: (state.onboardingForm as OnboardingForm).zipCode,
  };
};

const mapDispatchToProps = (dispatch: Dispatch) => {
  return {
    onCreateLead: (body: LeadAttributes) => dispatch(createLeadAction(body)),
    onEmailCheck: (email: string) => dispatch(emailCheck(email)),
    onSetOnboardingFormValues: (values: FormValues) => dispatch(setOnboardingFormValues(values)),
    segmentTrack: (event: UnifiedEvent, payload: any) => dispatch(segmentTrack(event, payload)),
    onAddOnboardingStepAfterGivenStepKey: (stepKey: STEP_KEY, step: ProgressStep) =>
      dispatch(addOnboardingStepAfterGivenStepKey(stepKey, step)),
  };
};

const connector = connect(mapStateToProps, mapDispatchToProps);

export const SignInLink = () => (
  <>
    There is already an account with this email. <LinkWithQuery to={login.toPath()}>Sign in</LinkWithQuery>
  </>
);

export interface SubmitProps {
  handleNextClick: () => void;
  onCreateLead: ({ zipCode, email, source }: { zipCode: string; email: string; source: string }) => void;
  onEmailCheck: (email: string) => Promise<any>;
  onSetOnboardingFormValues: ({ email }: { email: string }) => void;
  segmentTrack: SegmentTrackType;
  email: string;
  zipCode: string;
  onAddOnboardingStepAfterGivenStepKey: (arg0: STEP_KEY, arg1: ProgressStep) => void;
}

const handleSubmit = async (
  values: FormValues,
  { props, setErrors, setSubmitting }: FormikBag<SubmitProps, FormValues>
) => {
  const {
    handleNextClick,
    onCreateLead,
    onEmailCheck,
    onSetOnboardingFormValues,
    segmentTrack,
    zipCode,
    onAddOnboardingStepAfterGivenStepKey,
  } = props;
  const email = (values.email as string)?.toLowerCase().trim();

  setSubmitting(true);

  try {
    await onEmailCheck(email);
    segmentTrack(UNIFIED.captchaSuccessful, { type: 'Invisible' });
    segmentTrack(UNIFIED.createLead);
    onCreateLead({ zipCode, email, source: 'web_onboarding_form' });
    onSetOnboardingFormValues({ email });
    setOnboardingStorage({ email, zipCode });
    segmentTrack(UNIFIED.createEligibleLead);
    handleNextClick();
  } catch (error: any) {
    //captcha failed
    if (error.response?.status === 403) {
      segmentTrack(UNIFIED.captchaFailed, { type: 'Invisible ' });
      onSetOnboardingFormValues({ email });
      onAddOnboardingStepAfterGivenStepKey(STEP_KEY.CONTACT, CAPTCHA_STEP);
      handleNextClick();
      return;
    }
    // TODO:  @Fannie update based on new error shape from bff
    const errorMessage = {
      email: error.response?.status === 400 ? <SignInLink /> : 'Error validating email',
    } as FormikErrors<FormValues>;
    setErrors(errorMessage);
  }
};

const enhancedWithFormik = withFormik({
  mapPropsToValues: validation.initialValues,
  validationSchema: validation.schema,
  validateOnChange: true,
  validateOnBlur: false,
  validateOnMount: true,
  handleSubmit,
});

export const ContactStep = compose(connector, enhancedWithFormik)(UnconnectedContactStep);
